import useShortcut from './useShortcut';
import {
  ShortcutType,
  ShortcutGroupMessage,
  DefaultTriggers,
  DISABLED_TRIGGER,
} from '@scale/shared/types/preference';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';

import { SendTypes } from 'react-scale/components/scalers/tasks/common/SendTask';
import { TaskReviewOutcome } from '@scale/shared/defaults';

export function useReviewActionShortcuts(
  makeChanges: { action: () => void; disabled: boolean },
  rejectReview: { action: () => void; disabled: boolean },
  cancelAction: { action: () => void; disabled: boolean },
) {
  useShortcut({
    trigger: DefaultTriggers[ShortcutType.MakeChanges],
    action: makeChanges.action,
    disabled: makeChanges.disabled,
    description: 'Make changes',
    type: ShortcutType.MakeChanges,
    group: ShortcutGroupMessage.ReviewButtons,
    forceNotEditable: true,
  });

  useShortcut({
    trigger: DefaultTriggers[ShortcutType.RejectReview],
    action: rejectReview.action,
    disabled: rejectReview.disabled,
    description: 'Send attempt back to queue',
    type: ShortcutType.RejectReview,
    group: ShortcutGroupMessage.ReviewButtons,
    forceNotEditable: true,
  });

  useShortcut({
    trigger: DefaultTriggers[ShortcutType.CancelActionReview],
    action: cancelAction.action,
    disabled: cancelAction.disabled,
    description: 'Cancel current action',
    type: ShortcutType.CancelActionReview,
    group: ShortcutGroupMessage.ReviewButtons,
    forceNotEditable: true,
  });
}

export function useSkipActionShortcut(
  skip: { action: () => void; disabled: boolean },
  forReview = false,
) {
  useShortcut({
    trigger: DefaultTriggers[ShortcutType.SkipStudioTask],
    action: skip.action,
    disabled: skip.disabled,
    description: `Skip ${forReview ? 'review' : 'attempt'} task`,
    type: ShortcutType.SkipStudioTask,
    group: forReview ? ShortcutGroupMessage.ReviewButtons : ShortcutGroupMessage.AttemptButtons,
    forceNotEditable: true,
  });
}

export function useSendModalShortcut(onClick: () => void, sendType: SendTypes, disabled: boolean) {
  let trigger = DISABLED_TRIGGER;
  let description = '';
  let type: ShortcutType | undefined;
  let group = ShortcutGroupMessage.ReviewButtons;

  switch (sendType) {
    case SendTypes.Submit:
      trigger = DefaultTriggers[ShortcutType.SubmitTask];
      description = 'Submit';
      type = ShortcutType.SubmitTask;
      group = ShortcutGroupMessage.AttemptButtons;
      break;
    case SendTypes.Approve:
    case SendTypes.ApproveWithChanges:
      trigger = DefaultTriggers[ShortcutType.ApproveReview];
      description = 'Approve';
      type = ShortcutType.ApproveReview;
      break;
    case SendTypes.Reject:
    case SendTypes.SendBackToQueue:
    case SendTypes.SendBackToQueueWithChanges:
      trigger = DefaultTriggers[ShortcutType.RejectReview];
      description = 'Reject / send back to queue';
      type = ShortcutType.RejectReview;
      break;
  }

  useShortcut({
    trigger,
    action: onClick,
    description,
    type,
    group,
    disabled,
    forceNotEditable: !IS_FEDERAL,
  });
}

export function useAuditSubmitShortcut(
  onClick: () => void,
  auditType: TaskReviewOutcome,
  disabled: boolean,
) {
  let trigger = DISABLED_TRIGGER;
  let description = '';
  let type: ShortcutType | undefined;
  const group = ShortcutGroupMessage.ReviewButtons;

  switch (auditType) {
    case TaskReviewOutcome.Accepted:
      trigger = DefaultTriggers[ShortcutType.ApproveReview];
      description = 'Approve';
      type = ShortcutType.ApproveReview;
      break;
    case TaskReviewOutcome.Rejected:
      trigger = DefaultTriggers[ShortcutType.RejectReview];
      description = 'Reject / send back to queue';
      type = ShortcutType.RejectReview;
      break;
    case TaskReviewOutcome.Fixed:
      trigger = DefaultTriggers[ShortcutType.FixReview];
      description = 'Reject / send back to queue';
      type = ShortcutType.FixReview;
      break;
  }

  useShortcut({
    trigger,
    action: onClick,
    description,
    type,
    group,
    disabled,
    forceNotEditable: true,
  });
}
